/**
 * Idées d'accords, moments et cocktails — Vignerons de la Cave de Pierrefeu
 *
 * Lit une feuille Google Sheets publiée en CSV (colonnes attendues :
 * Cuvée, Couleur, Type, Titre, Description, Photo) et affiche des cartes.
 *
 * Deux modes, définis par data-mode sur le conteneur :
 * - "gallery" : affiche tout, avec filtres Couleur / Type (page dédiée)
 * - "product" : affiche uniquement les lignes dont la Cuvée correspond
 *   exactement à data-product-title (section fiche produit, sans filtres)
 */

(function () {
  class AccordsVigneron {
    constructor(root) {
      this.root = root;
      this.sheetUrl = root.dataset.sheetUrl;
      this.mode = root.dataset.mode || "gallery";
      this.productTitle = root.dataset.productTitle || "";
      this.limit = parseInt(root.dataset.limit, 10) || null;
      this.rows = [];
      this.activeColor = "Tous";
      this.activeCuisine = "Tous";
      this.activeMoment = "Tous";
      this.visibleCount = 10;
      this.init();
    }

    async init() {
      if (!this.sheetUrl) return;

      if (typeof Papa === "undefined") {
        console.error("PapaParse n'est pas chargé.");
        return;
      }

      Papa.parse(this.sheetUrl, {
        download: true,
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.replace(/^\uFEFF/, "").trim(),
        transform: (v) => (typeof v === "string" ? v.trim() : v),
        complete: (results) => {
          this.rows = results.data.filter((r) => r.Cuvée && r.Titre);
          if (this.mode === "product") {
            this.rows = this.rows.filter(
              (r) => r["Cuvée"].trim() === this.productTitle.trim()
            );
            if (this.rows.length === 0) {
              this.root.classList.add("av-hidden");
              return;
            }
          }
          if (this.mode === "carousel") {
            this.renderCarousel();
            return;
          }
          this.render();
        },
        error: () => {
          this.root.innerHTML = "";
        }
      });
    }

    getFiltered() {
      return this.rows.filter((r) => {
        const colorOk = this.activeColor === "Tous" || r["Couleur"] === this.activeColor;
        const cuisineOk = this.activeCuisine === "Tous" || this.splitValues(r["Cuisine"]).includes(this.activeCuisine);
        const momentOk = this.activeMoment === "Tous" || this.splitValues(r["Ambiance"]).includes(this.activeMoment);
        return colorOk && cuisineOk && momentOk;
      });
    }

    splitValues(cell) {
      return (cell || "")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }

    uniqueValues(column) {
      const seen = new Set();
      this.rows.forEach((r) => {
        this.splitValues(r[column]).forEach((v) => seen.add(v));
      });
      return ["Tous", ...Array.from(seen).sort((a, b) => a.localeCompare(b, "fr"))];
    }

    render() {
      const isGallery = this.mode === "gallery";
      this.root.innerHTML = `
        ${this.root.dataset.title ? `<h2 class="av-title">${this.root.dataset.title}</h2>` : ""}
        ${isGallery ? this.filtersHtml() : ""}
        <div class="av-grid" data-av-grid></div>
        ${isGallery ? '<div class="av-more" data-av-more></div>' : ""}
      `;

      if (isGallery) {
        this.root.querySelector('[data-av-select="color"]').addEventListener("change", (e) => {
          this.activeColor = e.target.value;
          this.visibleCount = 10;
          this.renderGrid();
        });
        this.root.querySelector('[data-av-select="cuisine"]').addEventListener("change", (e) => {
          this.activeCuisine = e.target.value;
          this.visibleCount = 10;
          this.renderGrid();
        });
        this.root.querySelector('[data-av-select="moment"]').addEventListener("change", (e) => {
          this.activeMoment = e.target.value;
          this.visibleCount = 10;
          this.renderGrid();
        });
      }

      this.renderGrid();
    }

    filtersHtml() {
      const colors = ["Tous", "Blanc", "Rosé", "Rouge"];
      const cuisines = this.uniqueValues("Cuisine");
      const moments = this.uniqueValues("Ambiance");
      const select = (label, items, attr, active) => `
        <div class="av-filter-select-wrap">
          <label class="av-filter-label">${label}</label>
          <select class="av-filter-select" data-av-select="${attr}">
            ${items.map((v) => `<option value="${v}"${v === active ? " selected" : ""}>${v}</option>`).join("")}
          </select>
        </div>
      `;
      return `
        <div class="av-filters">
          ${select("Couleur", colors, "color", this.activeColor)}
          ${select("Cuisine", cuisines, "cuisine", this.activeCuisine)}
          ${select("Ambiance", moments, "moment", this.activeMoment)}
        </div>
      `;
    }

    renderGrid() {
      const grid = this.root.querySelector("[data-av-grid]");
      const allItems = this.getFiltered();
      const items = this.mode === "gallery" ? allItems.slice(0, this.visibleCount) : allItems;

      if (items.length === 0) {
        grid.innerHTML = `<p class="av-empty">Aucune idée pour cette combinaison pour le moment.</p>`;
      } else {
        grid.innerHTML = items
          .map((r) => {
            const image = r["Photo"]
              ? `<div class="av-card-image"><img src="${r["Photo"]}" alt="${r["Titre"]}" loading="lazy"></div>`
              : "";
            const productUrl = (window.avProductLookup || {})[r["Cuvée"]];
            const cuveeLink = this.mode === "gallery"
              ? productUrl
                ? `<a class="av-card-cuvee" href="${productUrl}">${r["Cuvée"]}</a>`
                : `<p class="av-card-cuvee">${r["Cuvée"]}</p>`
              : "";
            return `
              <article class="av-card">
                ${image}
                <div class="av-card-body">
                  <p class="av-card-type">${r["Type"] || ""}</p>
                  <h3 class="av-card-title">${r["Titre"]}</h3>
                  ${r["Description"] ? `<p class="av-card-desc">${r["Description"]}</p>` : ""}
                  ${cuveeLink}
                </div>
              </article>
            `;
          })
          .join("");
      }

      if (this.mode === "gallery") {
        const moreEl = this.root.querySelector("[data-av-more]");
        if (allItems.length > this.visibleCount) {
          moreEl.innerHTML = `<button type="button" class="av-more-btn" data-av-more-btn>Voir plus d'idées</button>`;
          moreEl.querySelector("[data-av-more-btn]").addEventListener("click", () => {
            this.visibleCount += 10;
            this.renderGrid();
          });
        } else {
          moreEl.innerHTML = "";
        }
      }
    }

    renderCarousel() {
      const items = this.limit ? this.rows.slice(0, this.limit) : this.rows;
      const viewAllUrl = this.root.dataset.viewAllUrl;

      const cardsHtml = items
        .map((r) => {
          const image = r["Photo"]
            ? `<div class="av-card-image"><img src="${r["Photo"]}" alt="${r["Titre"]}" loading="lazy"></div>`
            : "";
          const productUrl = (window.avProductLookup || {})[r["Cuvée"]];
          const cuveeLink = productUrl
            ? `<a class="av-card-cuvee" href="${productUrl}">${r["Cuvée"]}</a>`
            : `<p class="av-card-cuvee">${r["Cuvée"]}</p>`;
          return `
            <article class="av-card av-carousel-card">
              ${image}
              <div class="av-card-body">
                <p class="av-card-type">${r["Type"] || ""}</p>
                <h3 class="av-card-title">${r["Titre"]}</h3>
                ${r["Description"] ? `<p class="av-card-desc">${r["Description"]}</p>` : ""}
                ${cuveeLink}
              </div>
            </article>
          `;
        })
        .join("");

      this.root.innerHTML = `
        ${this.root.dataset.title ? `<h2 class="av-title">${this.root.dataset.title}</h2>` : ""}
        <div class="av-carousel-wrap">
          <button type="button" class="av-carousel-arrow av-carousel-arrow--prev" data-av-prev aria-label="Précédent">‹</button>
          <div class="av-carousel-track" data-av-track>${cardsHtml}</div>
          <button type="button" class="av-carousel-arrow av-carousel-arrow--next" data-av-next aria-label="Suivant">›</button>
        </div>
        ${viewAllUrl ? `<div class="av-more"><a class="av-more-btn" href="${viewAllUrl}">Voir toutes les idées</a></div>` : ""}
      `;

      const track = this.root.querySelector("[data-av-track]");
      const step = () => track.querySelector(".av-carousel-card")?.offsetWidth + 20 || 280;
      this.root.querySelector("[data-av-prev]").addEventListener("click", () => {
        track.scrollBy({ left: -step(), behavior: "smooth" });
      });
      this.root.querySelector("[data-av-next]").addEventListener("click", () => {
        track.scrollBy({ left: step(), behavior: "smooth" });
      });
    }
  }

  document.querySelectorAll("[data-accords-vigneron]").forEach((el) => new AccordsVigneron(el));
})();